// Click-id tracking (TikTok ttclid + Meta fbclid).
//
// The old Home page persisted ttclid to a .viralgpt.com cookie. This keeps that
// behavior and adds fbclid the same way, and makes it work across the
// startviralgpt.com landing domain.
//
// Cross-domain reality: a cookie set on startviralgpt.com can never be read by
// viralgpt.com (separate root domains — a hard browser boundary). So we don't
// rely on the cookie crossing over. Instead:
//   - on viralgpt.com we write the shared .viralgpt.com cookie (as before), and
//   - on the landing domain we write a host-only cookie AND mainUrl() forwards
//     ttclid/fbclid on every CTA link, so when the visitor reaches viralgpt.com
//     the ids arrive in the URL and get cookied there on the right domain.

const THIRTY_DAYS = 60 * 60 * 24 * 30;

export const CLICK_ID_PARAMS = ["ttclid", "fbclid"];

// Write a tracking cookie on the correct scope for the current host.
export const setTrackingCookie = (name, value) => {
  const host = window.location.hostname;
  // Leading dot matters: "startviralgpt.com".endsWith("viralgpt.com") is true,
  // so match ".viralgpt.com" to avoid the landing domain setting a cookie the
  // browser would reject.
  const isMainSite = host === "viralgpt.com" || host.endsWith(".viralgpt.com");
  if (host === "localhost") {
    document.cookie = `${name}=${value};path=/;max-age=${THIRTY_DAYS};SameSite=Lax`;
  } else if (isMainSite) {
    document.cookie = `${name}=${value};path=/;domain=.viralgpt.com;max-age=${THIRTY_DAYS};Secure;SameSite=None`;
  } else {
    document.cookie = `${name}=${value};path=/;max-age=${THIRTY_DAYS};Secure;SameSite=Lax`;
  }
};

// Persist ttclid + fbclid from the current URL into cookies.
export const persistClickIds = () => {
  const params = new URLSearchParams(window.location.search);
  CLICK_ID_PARAMS.forEach((name) => {
    const value = params.get(name);
    if (value) setTrackingCookie(name, value);
  });
};

const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith(name + "="))
    ?.split("=")[1] || "";

// Read the current click ids (URL first, then cookie). Used to attach them to
// the /pixel request body — required on startviralgpt.com, whose host-only
// cookie is never sent cross-domain to api.viralgpt.com. Keys with no value are
// omitted (JSON.stringify drops undefined), so the payload stays clean.
export const getClickIds = () => {
  const params = new URLSearchParams(window.location.search);
  const out = {};
  CLICK_ID_PARAMS.forEach((name) => {
    const value = params.get(name) || getCookie(name);
    if (value) out[name] = value;
  });
  return out;
};

// Affiliate (?r=) for pixel bodies, URL first (fresh ad click) then cookie.
// Needed because home_view fires from a page's useEffect, which runs BEFORE
// LanguageProvider's effect has written the affiliate cookie — so the cookie
// alone misses the landing event (and the variant domain's host-only cookie
// can never be sent cross-domain to api.viralgpt.com at all).
export const getAffiliateParams = () => {
  const r = new URLSearchParams(window.location.search).get("r") || getCookie("affiliate");
  return r ? { affiliate: r } : {};
};

// ─── Funnel attribution ──────────────────────────────────────────────────────
// Which funnel a visitor is in is a property of their whole journey, not of the
// page they happen to be on: the 50off funnel spans startviralgpt.com → the
// visualizer → /50off. So the tag is decided once on entry and kept in
// sessionStorage, then attached to every pixel event for the rest of the visit.
//
// Kept in sessionStorage (not localStorage) so a later visit down a different
// path is attributed to that path instead of inheriting a stale tag.
// startviralgpt.com sends visitors to viralgpt.com/visualizer, and
// sessionStorage is per-origin — so the funnel tag set on the variant domain
// can't be read once they land. mainUrl() stamps this param on the hop instead.
export const VARIANT_ENTRY_PARAM = "fromstart";
const VARIANT_ENTRY_KEY = "from_variant_funnel";

// True for a visitor who arrived from startviralgpt.com. Sticky for the
// session, so it survives once the param drops off the URL.
export const cameFromVariant = () => {
  try {
    if (new URLSearchParams(window.location.search).get(VARIANT_ENTRY_PARAM) === "1") {
      sessionStorage.setItem(VARIANT_ENTRY_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(VARIANT_ENTRY_KEY) === "1";
  } catch (e) {
    return new URLSearchParams(window.location.search).get(VARIANT_ENTRY_PARAM) === "1";
  }
};

const FUNNEL_KEY = "funnel_tag";
export const FUNNEL_50OFF = "50off";
export const FUNNEL_MAIN = "main";

// 50off traffic used to be split by entry page into "50off-pricing" and
// "50off-visualizer". The split stopped meaning anything once the variant home
// became a full landing page: every rise- ad click was force-tagged as a
// pricing entry on arrival and kept that tag through the visualizer, so one
// value covered both paths and the other was near-empty. Everything in the
// funnel is now tagged "50off".
//
// The two old values still exist in the data and are still selectable in the
// admin journey filter, so history stays readable — nothing new is written
// with them.
const is50off = (tag) => typeof tag === "string" && tag.startsWith(FUNNEL_50OFF);

const detectFunnel = () => {
  const host = window.location.hostname;
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  // An explicit ?funnel= wins: mainUrl() stamps it on the variant → main-site
  // hop so the tag survives the domain change (it lives in per-origin
  // sessionStorage and can't cross on its own).
  const explicit = params.get("funnel");
  if (is50off(explicit)) return explicit;
  // The variant landing domain and the one-page checkout are both 50off, as is
  // anyone who arrived on the main domain from the variant funnel.
  const inFunnel =
    host.includes("startviralgpt.com") ||
    path.startsWith("/50off") ||
    cameFromVariant();
  if (!inFunnel) return FUNNEL_MAIN;
  return FUNNEL_50OFF;
};

// Call once per page load, before any pixel event fires.
export const initFunnel = () => {
  try {
    const existing = sessionStorage.getItem(FUNNEL_KEY);
    if (existing) {
      // A visitor who reaches /50off from the main site has entered the 50off
      // funnel — upgrade the tag rather than keeping the entry-page value.
      const detected = detectFunnel();
      if (!is50off(existing) && is50off(detected)) {
        sessionStorage.setItem(FUNNEL_KEY, detected);
      }
      return;
    }
    sessionStorage.setItem(FUNNEL_KEY, detectFunnel());
  } catch (e) { /* sessionStorage unavailable — getFunnel() falls back */ }
};

// Does the upgrade itself rather than relying on initFunnel() having run:
// React fires child effects before parent effects, so a page's own pixel call
// can happen before the provider's initFunnel(). Without this, the first events
// on /50off would inherit the previous page's "main" tag.
export const getFunnel = () => {
  try {
    const stored = sessionStorage.getItem(FUNNEL_KEY);
    const detected = detectFunnel();
    if (is50off(detected) && !is50off(stored)) {
      sessionStorage.setItem(FUNNEL_KEY, detected);
      return detected;
    }
    return stored || detected;
  } catch (e) {
    return detectFunnel();
  }
};
